import { ApiDomain } from "@incendium/api";
import { Box, Stack, Switch, Typography } from "@mui/material";
import AnimatedList from "Components/AnimatedList/AnimatedList";
import Loading from "Components/Loading/Loading";
import { AnimatePresence, motion } from "motion/react";
import { isEmpty } from "Helpers/isEmpty";
import produce from "immer";
import { CallbackOrVal } from "Interfaces";
import { Suspense, useCallback, useMemo } from "react";
import { TextValidator } from "react-material-ui-form-validator";

function ProjectDomainsHead() {
  return (
    <Stack direction={"row"} spacing={2} justifyContent="space-between">
      <Typography variant="subtitle2">Sub Domains</Typography>
      <Typography variant="subtitle2" sx={{ width: 115 }}>
        Enable Cross Link
      </Typography>
    </Stack>
  );
}

interface IProjectDomainsRowProps {
  row: ApiDomain;
  onEdit: (v: ApiDomain) => void;
  isRequired?: boolean;
}

function ProjectDomainsRow({
  row,
  onEdit,
  isRequired,
}: IProjectDomainsRowProps) {
  return (
    <Stack
      sx={{ width: "100%" }}
      direction="row"
      spacing={2}
      justifyContent="space-between"
    >
      <Box sx={{ flex: 1 }}>
        <TextValidator
          variant="outlined"
          placeholder="https://mydomain.com"
          label="Domain *"
          helper
          fullWidth
          name="domain"
          size="small"
          value={row.url || ""}
          validators={isRequired ? ["required", "isDomain"] : ["isDomain"]}
          errorMessages={
            isRequired
              ? ["Domain Name is required", "Enter a valid domain"]
              : ["Enter a valid domain"]
          }
          onChange={(e: any) =>
            onEdit(
              produce(row, (draft) => {
                draft.url = e.target.value;
              })
            )
          }
        />
      </Box>
      <Box sx={{ width: 115 }}>
        <Switch
          checked={row.crossDomainEnabled}
          onChange={(_, c) =>
            onEdit(
              produce(row, (draft) => {
                draft.crossDomainEnabled = c;
              })
            )
          }
        />
      </Box>
    </Stack>
  );
}

interface IProjectDomainsProps {
  domains: ApiDomain[];
  setDomains: (v: CallbackOrVal<ApiDomain[]>) => void;
}

function ProjectDomains({ domains, setDomains }: IProjectDomainsProps) {
  const onAdd = useCallback(() => {
    setDomains((prevDomains) =>
      produce(prevDomains, (draft) => {
        draft.push({
          id: 0 - draft.length,
          crossDomainEnabled: true,
        });
      })
    );
  }, [setDomains]);

  const onEdit = useCallback(
    (idx: number) => (v: ApiDomain) => {
      setDomains((prevDomains) =>
        produce(prevDomains, (draft) => {
          if (!draft[idx]) {
            draft[idx] = {};
          }
          draft[idx] = { ...v };
        })
      );
    },
    [setDomains]
  );

  const onRemove = useCallback(
    (idx: number, value: ApiDomain) => {
      setDomains((prevDomains) =>
        produce(prevDomains, (draft) => {
          const index = draft.findIndex((d) => d.id === value.id);
          draft?.splice(index, 1);
        })
      );
    },
    [setDomains]
  );

  // remove first but keep indexes
  const subdomains = useMemo(() => {
    const domainsCopy: any = [...(domains || [])];
    if (domainsCopy.length > 0) {
      domainsCopy[0] = null;
    }
    return domainsCopy;
  }, [domains]);

  return (
    <Suspense fallback={<Loading />}>
      <Stack
        direction={"row"}
        spacing={2}
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="subtitle2">Primary Domain</Typography>
        <Typography variant="subtitle2" sx={{ width: 115 }}>
          Enable Cross Link
        </Typography>
      </Stack>
      <ProjectDomainsRow
        row={domains[0] || { id: 0 }}
        onEdit={(v) => onEdit(0)(v)}
        isRequired
      />
      <AnimatePresence mode="popLayout" initial={true}>
        {!isEmpty(domains[0] || {}) && domains[0].url !== "" && (
          <Stack
            component={motion.div}
            initial={{ x: -100, scale: 0.8, opacity: 0 }}
            animate={{ x: 0, scale: 1, opacity: 1 }}
            exit={{ x: -100, scale: 0.8, opacity: 0 }}
          >
            <Typography variant="subtitle2" my={2}>
              Sub Domains
            </Typography>
            <AnimatedList
              onAdd={onAdd}
              onRemove={onRemove}
              onEdit={onEdit}
              items={
                subdomains?.length > 1
                  ? subdomains
                  : [null, { id: -2, crossDomainEnabled: true }]
              }
              uniqueProp={"id"}
              RowHead={!true ? ProjectDomainsHead : undefined}
              RowComponent={ProjectDomainsRow}
              rowSpacing={3}
              btnText="Add Subdomain"
            />
          </Stack>
        )}
      </AnimatePresence>
    </Suspense>
  );
}

export default ProjectDomains;
